import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";

const TestResult = () => {
  const { resultId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        let result;
        
        // Check if it's a local result (frontend-only)
        if (resultId.startsWith('local-')) {
          const storedResult = localStorage.getItem('lastTestResult');
          if (storedResult) {
            result = JSON.parse(storedResult);
          } else {
            throw new Error('Local result not found');
          }
        } else {
          throw new Error("Backend results are not available. Please use local results only."); 
        }
    
        setResult(result);
      } catch (error) {
        console.error("Error fetching result:", error);
        setError("Не удалось загрузить результаты теста");
      } finally {
        setLoading(false);
      }
    };

    if (resultId) {
      fetchResult();
    }
  }, [resultId]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Обработка результатов...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-red-50 p-6 rounded-lg">
            <p className="text-red-600">{error}</p>
            <Link
              to="/tests"
              className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Вернуться к тестам
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600">Результаты не найдены</p>
        </div>
      </div>
    );
  }

  const getTestTitle = () => {
    switch (result.test_type) {
      case "natural_intelligence": return "Тест по природным интеллектам";
      case "intelligence_types": return "Анкета по типам интеллекта";
      case "belbin": return "Анкета по Белбину";
      case "ors": return "Тест на определение ОРС";
      case "subtype": return "Тест на подтип личности";
      default: return "Тест";
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white p-8 rounded-lg shadow-md mb-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Тест завершен успешно!
            </h1>
            <p className="text-lg text-gray-600 mb-2">
              {getTestTitle()}
            </p>
          </div>
          
          <div className="mb-8">
            {result.user_info && (
              <div className="bg-blue-50 p-6 rounded-lg mb-6">
                <h3 className="font-semibold text-blue-800 mb-3">Информация об участнике:</h3>
                <div className="space-y-2 text-blue-700">
                  <p><strong>Имя:</strong> {result.user_info.full_name}</p>
                  <p><strong>Email:</strong> {result.user_info.email}</p>
                  <p><strong>Телефон:</strong> {result.user_info.phone}</p>
                </div>
              </div>
            )}
            
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h3 className="font-semibold text-green-800 mb-3">✅ Что произошло:</h3>
              <ul className="text-green-700 space-y-2">
                <li>• Ваши ответы на вопросы теста были успешно сохранены</li>
                <li>• Результаты отправлены на email для анализа специалистами</li>
                <li>• В ближайшее время с вами свяжутся для обсуждения результатов</li>
              </ul>
            </div>

            <div className="bg-yellow-50 p-6 rounded-lg mb-6">
              <h3 className="font-semibold text-yellow-800 mb-3">📧 Обратная связь:</h3>
              <p className="text-yellow-700 mb-3">
                Для получения детального анализа результатов и персональных рекомендаций свяжитесь с нами:
              </p>
              <div className="space-y-2 text-yellow-700">
                <p><strong>Телефон:</strong> +7(992)300-65-46</p>
                <p><strong>Email:</strong> developskillsnow@yandex.ru</p>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3">💡 Важно помнить:</h3>
              <p className="text-gray-700">
                Результаты теста показывают ваши текущие склонности и предпочтения. 
                Помните, что эти результаты могут меняться со временем и под влиянием различных факторов.
              </p>
            </div>
          </div>

          <div className="text-center pt-6 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/tests"
                className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Пройти другие тесты
              </Link>
              <Link
                to="/"
                className="bg-gray-600 text-white px-6 py-3 rounded-md hover:bg-gray-700 transition-colors font-medium"
              >
                Вернуться на главную
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestResult;